import  { useState, useEffect } from 'react';
import { Users, Wallet, Flag, Search, Filter, MoreVertical, ChevronLeft, ChevronRight, Bell } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { useApp } from "@/contexts/AppContext";
import { REFERRAL_HOTSPOT, REFERRAL_STATS, REFERRAL_GRAPH } from '../../api/services/adminservice';
import { useTranslation } from 'react-i18next';




const AdminReferrals = () => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  
  const { theme } = useApp();
 
  
  const fraudData = [
    { month: 'Jan', suspicious: 120, confirmed: 45 },
    { month: 'Feb', suspicious: 135, confirmed: 38 },
    { month: 'Mar', suspicious: 100, confirmed: 58 },
    { month: 'Apr', suspicious: 135, confirmed: 52 },
    { month: 'May', suspicious: 90, confirmed: 62 },
    { month: 'Jun', suspicious: 230, confirmed: 35 },
    { month: 'Jul', suspicious: 205, confirmed: 75 }
  ];

  const referrals = [
    {
      referrerName: 'John Smith',
      referrerEmail: 'john@example.com',
      refereeName: 'John Smith',
      refereeEmail: 'john@example.com',
      date: 'Jan 15, 2024',
      status: 'Approved'
    },
    {
      referrerName: 'John Smith',
      referrerEmail: 'john@example.com',
      refereeName: 'John Smith',
      refereeEmail: 'john@example.com',
      date: 'Jan 15, 2024',
      status: 'Pending'
    }
  ];

  const isDark = theme === 'dark';
  interface ReferralStats {
    totalReferrals?: { count: number };
    totalKXTEarned?: { amount: number; percentageChange: number };
    flaggedReferrals?: { count: number };
  }
  
  interface ReferralEntry {
    referrerName?: string;
    referrerEmail?: string;
    refereeName?: string;
    refereeEmail?: string;
    referralDate?: string;
    status?: string;
  }
  
  interface FraudChartData {
    month?: string;
    suspiciousActivities?: number;
    confirmedFraud?: number;
  }

  const [statsLoading, setStatsLoading] = useState(true);
  const [referralStats, setReferralStats] = useState<ReferralStats | null>(null);
  const [referralHotspots, setReferralHotspots] = useState<ReferralEntry[]>([]);
  const [referralGraph, setReferralGraph] = useState<FraudChartData[]>([]);

  async function fetchReferralStats() {
    try {
      const data = await REFERRAL_STATS();
      console.log('Referral stats data:', data);
      const resData = (data as { data?: ReferralStats }).data;
      setReferralStats(resData || null);
    } catch (error) {
      console.error('Error fetching referral stats:', error);
    }
  }

  async function fetchReferralHotspot() {
    try {
      const data = await REFERRAL_HOTSPOT();
      console.log('Referral hotspot data:', data);
      const resData = (data as { data?: { referrals?: ReferralEntry[] } }).data;
      setReferralHotspots(resData?.referrals || []);
    } catch (error) {
      console.error('Error fetching referral hotspot:', error);
    }
  }

  async function fetchReferralGraph() {
    try {
      const data = await REFERRAL_GRAPH({ period: "last_7_days" });
      console.log('Referral graph data:', data);
      const resData = (data as { data?: { chartData?: FraudChartData[] } }).data;
      setReferralGraph(resData?.chartData || []);
    } catch (error) {
      console.error('Error fetching referral graph:', error);
    }
  }

  useEffect(() => {
    async function fetchData() {
      setStatsLoading(true);
      await fetchReferralStats();
      await fetchReferralHotspot();
      await fetchReferralGraph()
      setStatsLoading(false);
    }
    fetchData();

  }, []);
  return (
    <div className={`min-h-screen ${isDark ? 'bg-black' : 'bg-gray-50'}`}>
      <div className="p-4 sm:p-6 lg:p-8">
        {/* Metrics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
          {/* Total Referrals */}
          <div className={`${isDark ? 'bg-[#333436] border-gray-700 shadow-lg' : 'bg-white border-gray-200 shadow-sm'} rounded-lg p-4 sm:p-6 border`}>
            <div className="flex items-start justify-between">
              <div>
                <p className={`text-xs sm:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`}>{t('admin.referrals.metrics.totalReferrals', { defaultValue: 'Total Referrals' })}</p>
                <div className="flex items-baseline gap-2 mt-1">
                  {statsLoading ? (
                    <div className={`h-8 w-16 rounded animate-pulse ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}></div>
                  ) : (
                    <>
                      <span className={`text-2xl sm:text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{referralStats?.totalReferrals?.count || 0}</span>
                      <span className={`text-xs sm:text-sm ${isDark ? 'text-green-500' : 'text-green-600'} font-medium`}>↑8.1%</span>
                    </>
                  )}
                </div>
              </div>
              <div className={`${isDark ? 'bg-gray-700' : 'bg-gray-100'} p-2 rounded-lg`}>
                <Users className={`w-4 h-4 sm:w-5 sm:h-5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`} />
              </div>
            </div>
          </div>

          {/* Total KXT Earned */}
          <div className={`${isDark ? 'bg-[#333436] border-gray-700 shadow-lg' : 'bg-white border-gray-200 shadow-sm'} rounded-lg p-4 sm:p-6 border`}>
            <div className="flex items-start justify-between">
              <div>
                <p className={`text-xs sm:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`}>{t('admin.referrals.metrics.totalKxtEarned', { defaultValue: 'Total KXT Earned' })}</p>
                <div className="flex items-baseline gap-2 mt-1">
                  {statsLoading ? (
                    <div className={`h-8 w-16 rounded animate-pulse ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}></div>
                  ) : (
                    <>
                      <span className={`text-2xl sm:text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{referralStats?.totalKXTEarned?.amount || 0}</span>
                      <span className={`text-xs sm:text-sm ${isDark ? 'text-green-500' : 'text-green-600'} font-medium`}>↑{referralStats?.totalKXTEarned?.percentageChange || 0}%</span>
                    </>
                  )}
                </div>
              </div>
              <div className={`${isDark ? 'bg-gray-700' : 'bg-gray-100'} p-2 rounded-lg`}>
                <Wallet className={`w-4 h-4 sm:w-5 sm:h-5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`} />
              </div>
            </div>
          </div>

          {/* Flagged Referrals */}
          <div className={`${isDark ? 'bg-[#333436] border-gray-700 shadow-lg' : 'bg-white border-gray-200 shadow-sm'} rounded-lg p-4 sm:p-6 border sm:col-span-2 lg:col-span-1`}>
            <div className="flex items-start justify-between">
              <div>
                <p className={`text-xs sm:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`}>{t('admin.referrals.metrics.flaggedReferrals', { defaultValue: 'Flagged Referrals' })}</p>
                <div className="flex items-baseline gap-2 mt-1">
                  {statsLoading ? (
                    <div className={`h-8 w-16 rounded animate-pulse ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}></div>
                  ) : (
                    <>
                      <span className={`text-2xl sm:text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{referralStats?.flaggedReferrals?.count || 0}</span>
                      <span className={`text-xs sm:text-sm ${isDark ? 'text-red-500' : 'text-red-600'} font-medium`}>↑8.1%</span>
                    </>
                  )}
                </div>
              </div>
              <div className={`${isDark ? 'bg-gray-700' : 'bg-gray-100'} p-2 rounded-lg`}>
                <Flag className={`w-4 h-4 sm:w-5 sm:h-5 ${isDark ? 'text-red-500' : 'text-red-600'}`} />
              </div>
            </div>
          </div>
        </div>

        {/* Hotspots List */}
        <div className={`${isDark ? 'bg-[#333436] border-gray-700 shadow-lg' : 'bg-white border-gray-200 shadow-sm'} rounded-lg border mb-6 sm:mb-8`}>
          <div className={`p-4 sm:p-6 ${isDark ? 'border-gray-700' : 'border-gray-200'} border-b`}>
            <h2 className={`text-lg sm:text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-4`}>{t('admin.referrals.list.title', { defaultValue: 'Hotspots List' })}</h2>
            
            {/* Search and Filter */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 relative">
                <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                <input
                  type="text"
                  placeholder={t('admin.referrals.list.searchPlaceholder', { defaultValue: 'Search products...' })}
                  className={`w-full pl-9 sm:pl-10 pr-4 py-2 sm:py-2.5 ${
                    isDark 
                      ? 'bg-[#1a1b1d] border-gray-600 text-white placeholder-gray-500' 
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                  } border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm`}
                />
              </div>
              <button className={`px-4 py-2 sm:py-2.5 ${
                isDark 
                  ? 'border-gray-600 bg-[#1a1b1d] hover:bg-gray-700 text-white' 
                  : 'border-gray-300 bg-white hover:bg-gray-50 text-gray-700'
              } border rounded-lg flex items-center justify-center gap-2`}>
                <span className="text-sm font-medium">{t('admin.referrals.list.allStatus', { defaultValue: 'All Status' })}</span>
              </button>
              <button className={`px-4 py-2 sm:py-2.5 ${
                isDark 
                  ? 'bg-white text-black hover:bg-gray-200' 
                  : 'bg-black text-white hover:bg-gray-800'
              } rounded-lg flex items-center justify-center gap-2`}>
                <Filter className="w-4 h-4" />
                <span className="text-sm font-medium">{t('admin.referrals.list.filter', { defaultValue: 'Filter' })}</span>
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px]">
              <thead className={`${isDark ? 'bg-[#1a1b1d] border-gray-700' : 'bg-gray-50 border-gray-200'} border-b`}>
                <tr>
                  <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} uppercase tracking-wider`}>
                    {t('admin.referrals.table.referrerName', { defaultValue: 'Referrer Name' })}
                  </th>
                  <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} uppercase tracking-wider`}>
                    {t('admin.referrals.table.refereeName', { defaultValue: 'Referee Name' })}
                  </th>
                  <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} uppercase tracking-wider`}>
                    {t('admin.referrals.table.referralDate', { defaultValue: 'Referral Date' })}
                  </th>
                  <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} uppercase tracking-wider`}>
                    {t('admin.referrals.table.status', { defaultValue: 'Status' })}
                  </th>
                  <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} uppercase tracking-wider`}>
                    {t('admin.referrals.table.actions', { defaultValue: 'Actions' })}
                  </th>
                </tr>
              </thead>
              <tbody className={`${isDark ? 'bg-[#333436] divide-gray-700' : 'bg-white divide-gray-200'} divide-y`}>
                {statsLoading && (
                  <tr>
                    <td colSpan={5} className={`px-3 sm:px-6 py-8 text-center ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      {t('admin.referrals.table.loading', { defaultValue: 'Loading referrals...' })}
                    </td>
                  </tr>
                )}
                {!statsLoading && (!referralHotspots || referralHotspots.length === 0) && (
                  <tr>
                    <td colSpan={5} className={`px-3 sm:px-6 py-4 text-center ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      {t('admin.referrals.table.noReferralsFound', { defaultValue: 'No referrals found.' })}
                    </td>
                  </tr>
                )}
                {!statsLoading && referralHotspots?.map((ref, idx) => {
                  return (
                  <tr key={idx} className={isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className={`w-8 h-8 ${isDark ? 'bg-gray-600' : 'bg-gray-200'} rounded-full flex items-center justify-center mr-2 sm:mr-3 flex-shrink-0`}>
                          <span className={`text-xs sm:text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-600'}`}>JS</span>
                        </div>
                        <div className="min-w-0">
                          <div className={`text-xs sm:text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'} truncate`}>{ref.referrerName}</div>
                          <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'} truncate`}>{ref.referrerEmail}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className={`w-8 h-8 ${isDark ? 'bg-gray-600' : 'bg-gray-200'} rounded-full flex items-center justify-center mr-2 sm:mr-3 flex-shrink-0`}>
                          <span className={`text-xs sm:text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-600'}`}>JS</span>
                        </div>
                        <div className="min-w-0">
                          <div className={`text-xs sm:text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'} truncate`}>{ref.refereeName}</div>
                          <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'} truncate`}>{ref.refereeEmail}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                      <div className={`text-xs sm:text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>{ref.referralDate ? new Date(ref.referralDate).toLocaleDateString() : 'N/A'}</div>
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 sm:px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        String(ref.status).toLowerCase() === 'approved' 
                          ? isDark ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800'
                          : isDark ? 'bg-yellow-900 text-yellow-300' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {String(ref.status).toLowerCase() === 'approved'
                          ? t('admin.common.approved', { defaultValue: 'Approved' })
                          : t('admin.common.pending', { defaultValue: 'Pending' })}
                      </span>
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button className={`${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'}`}>
                        <MoreVertical className="w-4 h-4 sm:w-5 sm:h-5" />
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className={`px-3 sm:px-6 py-4 ${isDark ? 'border-gray-700' : 'border-gray-200'} border-t flex flex-col sm:flex-row items-center justify-between gap-3`}>
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1 ${
                isDark 
                  ? 'bg-[#1a1b1d] text-gray-300 border-gray-600' 
                  : 'bg-gray-100 text-gray-700 border-gray-300'
              } text-xs sm:text-sm rounded border`}>
                {t('admin.referrals.pagination.showing', { defaultValue: 'Showing' })}
              </span>
              <span className={`text-xs sm:text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{t('admin.referrals.pagination.range', { defaultValue: '1 to 3 of results' })}</span>
            </div>
            <div className="flex items-center gap-2">
              <button className={`p-2 ${isDark ? 'border-gray-600 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-50'} border rounded-lg`}>
                <ChevronLeft className={`w-3 h-3 sm:w-4 sm:h-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} />
              </button>
              <button className="px-3 py-1 bg-blue-600 text-white rounded-lg text-xs sm:text-sm font-medium">
                1
              </button>
              <button className={`px-3 py-1 ${
                isDark 
                  ? 'border-gray-600 hover:bg-gray-700 text-gray-300' 
                  : 'border-gray-300 hover:bg-gray-50 text-gray-700'
              } border rounded-lg text-xs sm:text-sm font-medium`}>
                2
              </button>
              <button className={`px-3 py-1 ${
                isDark 
                  ? 'border-gray-600 hover:bg-gray-700 text-gray-300' 
                  : 'border-gray-300 hover:bg-gray-50 text-gray-700'
              } border rounded-lg text-xs sm:text-sm font-medium`}>
                3
              </button>
              <button className={`p-2 ${isDark ? 'border-gray-600 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-50'} border rounded-lg`}>
                <ChevronRight className={`w-3 h-3 sm:w-4 sm:h-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Fraud Detection Analysis */}
        <div className={`${isDark ? 'bg-[#333436] border-gray-700 shadow-lg' : 'bg-white border-gray-200 shadow-sm'} rounded-lg border p-4 sm:p-6`}>
          <h2 className={`text-lg sm:text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-4 sm:mb-6`}>{t('admin.referrals.fraudDetectionTitle', { defaultValue: 'Fraud Detection Analysis' })}</h2>
          
          <div className="h-64 sm:h-80">
            {statsLoading ? (
              <div className={`w-full h-full rounded animate-pulse ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}></div>
            ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={referralGraph}>
                <defs>
                  <linearGradient id="colorSuspicious" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorConfirmed" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#4a4a4c' : '#f0f0f0'} />
                <XAxis 
                  dataKey="month" 
                  tick={{ fill: isDark ? '#9ca3af' : '#6b7280', fontSize: 11 }}
                  axisLine={{ stroke: isDark ? '#4a4a4c' : '#e5e7eb' }}
                />
                <YAxis 
                  tick={{ fill: isDark ? '#9ca3af' : '#6b7280', fontSize: 11 }}
                  axisLine={{ stroke: isDark ? '#4a4a4c' : '#e5e7eb' }}
                  domain={[0, 250]}
                  ticks={[0, 50, 100, 150, 200, 250]}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: isDark ? '#1a1b1d' : 'white', 
                    border: `1px solid ${isDark ? '#4a4a4c' : '#e5e7eb'}`,
                    borderRadius: '8px',
                    boxShadow: isDark ? '0 4px 6px -1px rgba(0, 0, 0, 0.3)' : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    fontSize: '12px',
                    color: isDark ? '#ffffff' : '#000000'
                  }}
                />
                <Legend 
                  verticalAlign="top" 
                  height={36}
                  iconType="circle"
                  wrapperStyle={{ fontSize: '12px', color: isDark ? '#ffffff' : '#000000' }}
                  formatter={(value) => {
                    return value === 'suspiciousActivities'
                      ? t('admin.referrals.chart.suspiciousActivities', { defaultValue: 'Suspicious Activities' })
                      : t('admin.referrals.chart.confirmedFraud', { defaultValue: 'Confirmed Fraud' });
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="suspiciousActivities"
                  stroke="#8b5cf6"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorSuspicious)"
                />
                <Area
                  type="monotone"
                  dataKey="confirmedFraud"
                  stroke="#ef4444"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorConfirmed)"
                />
              </AreaChart>
            </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminReferrals;
